(() => {
  const initialized = new WeakSet();

  function initialize(scope = document) {
    scope.querySelectorAll('[data-quantity]').forEach(group => {
      if (initialized.has(group)) return;
      initialized.add(group);
      const input = group.querySelector('input');
      if (!input) return;
      const bounds = () => ({
        min: Number(input.min || 0),
        max: input.max === '' ? Infinity : Number(input.max),
      });
      const sync = () => {
        const { min, max } = bounds();
        const value = Number(input.value);
        group.querySelector('[data-step="-1"]').disabled = value <= min;
        group.querySelector('[data-step="1"]').disabled = value >= max;
      };
      group.querySelectorAll('[data-step]').forEach(button => {
        button.addEventListener('click', () => {
          const { min, max } = bounds();
          input.value = String(Math.min(max, Math.max(min, Number(input.value || min) + Number(button.dataset.step))));
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
      input.addEventListener('change', () => {
        const { min, max } = bounds();
        input.value = String(Math.min(max, Math.max(min, Math.trunc(Number(input.value || min)))));
        sync();
      });
      input.addEventListener('input', sync);
      sync();
    });

    scope.querySelectorAll('[data-catalog]').forEach(catalog => {
      if (initialized.has(catalog)) return;
      initialized.add(catalog);
      const grid = catalog.querySelector('[data-product-grid]');
      if (!grid) return;
      const cards = Array.from(grid.querySelectorAll('.product-card'));
      cards.forEach((card, index) => { card.dataset.position = String(index); });
      const count = catalog.querySelector('[data-count]');
      const filters = catalog.querySelectorAll('[data-filter]');
      let filter = 'all';
      const apply = () => {
        const sort = catalog.querySelector('[data-sort]')?.value || 'featured';
        const ordered = [...cards].sort((a, b) => sort === 'featured'
          ? Number(a.dataset.position) - Number(b.dataset.position)
          : (Number(a.dataset.price) - Number(b.dataset.price)) * (sort === 'low' ? 1 : -1));
        ordered.forEach(card => {
          card.hidden = filter !== 'all' && card.dataset.category !== filter;
          grid.append(card);
        });
        if (count) count.textContent = catalog.dataset.countTemplate.replace('__COUNT__', String(cards.filter(card => !card.hidden).length));
      };
      filters.forEach(button => button.addEventListener('click', () => {
        filter = button.dataset.filter;
        filters.forEach(item => {
          const selected = item === button;
          item.setAttribute('aria-pressed', String(selected));
          item.classList.toggle('selected', selected);
        });
        apply();
      }));
      catalog.querySelector('[data-sort]')?.addEventListener('change', apply);
      apply();
    });

    scope.querySelectorAll('[data-product-section]').forEach(section => {
      if (initialized.has(section)) return;
      initialized.add(section);
      const selector = section.querySelector('[data-variant-select]');
      const form = section.querySelector('form.pdp-purchase');
      const singleVariant = form?.querySelector('input[name="id"]');
      const add = form?.querySelector('[name="add"]');
      const buy = form?.querySelector('[data-buy-now]');
      const error = form?.querySelector('[data-product-error]');
      const availability = section.querySelector('[data-product-availability]');
      const updatePurchaseState = () => {
        const option = selector?.selectedOptions[0] || singleVariant;
        const available = option?.dataset.available === 'true' && Number(option?.dataset.stock) > 0;
        if (add) add.disabled = !available;
        if (buy) buy.disabled = !available || buy.hasAttribute('aria-busy');
        if (availability) availability.textContent = available ? availability.dataset.inStock : availability.dataset.unavailable;
        return available;
      };
      const quantity = form?.querySelector('input[name="quantity"]');
      const image = section.querySelector('[data-main-image]');
      const thumbnails = section.querySelectorAll('[data-photo]');
      const dialog = section.querySelector('[data-zoom-dialog]');
      const zoomImage = section.querySelector('[data-zoom-image]');
      const showPhoto = url => {
        if (!image || !url) return;
        image.src = url;
        if (zoomImage) zoomImage.src = url;
        thumbnails.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.photo === url)));
      };
      thumbnails.forEach(button => button.addEventListener('click', () => showPhoto(button.dataset.photo)));
      section.querySelector('[data-zoom]')?.addEventListener('click', () => {
        if (typeof dialog?.showModal === 'function') dialog.showModal();
      });
      section.querySelector('[data-zoom-close]')?.addEventListener('click', () => dialog?.close());
      dialog?.addEventListener('click', event => {
        if (event.target !== dialog) return;
        const bounds = dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) dialog.close();
      });
      selector?.addEventListener('change', () => {
        const option = selector.selectedOptions[0];
        if (!option) return;
        updatePurchaseState();
        const price = section.querySelector('[data-product-price]');
        const sku = section.querySelector('[data-product-sku]');
        if (price) price.textContent = option.dataset.price;
        if (sku) sku.textContent = option.dataset.sku;
        if (quantity) {
          quantity.max = option.dataset.stock;
          quantity.dispatchEvent(new Event('change', { bubbles: true }));
        }
        showPhoto(option.dataset.image);
        const url = new URL(window.location.href);
        url.searchParams.set('variant', selector.value);
        window.history.replaceState({}, '', url);
      });

      updatePurchaseState();
      form?.addEventListener('submit', event => {
        if (!updatePurchaseState()) event.preventDefault();
      });
      if (buy) {
        buy.hidden = false;
        buy.addEventListener('click', async () => {
          if (!updatePurchaseState() || !form.reportValidity() || buy.disabled) return;
          buy.disabled = true;
          buy.setAttribute('aria-busy', 'true');
          if (error) error.hidden = true;
          try {
            const response = await fetch(buy.dataset.url, {
              method: 'POST',
              credentials: 'same-origin',
              headers: { Accept: 'application/json' },
              body: new FormData(form),
            });
            if (!response.ok) throw new Error('Cart could not be updated');
            window.location.assign(buy.dataset.checkout);
          } catch {
            if (error) error.hidden = false;
            buy.removeAttribute('aria-busy');
            updatePurchaseState();
          }
        });
      }
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize());
  else initialize();
  document.addEventListener('shopify:section:load', event => initialize(event.target));
})();
